import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  HostListener,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { PdfViewerComponent, PageRenderedEvent } from './pdf-viewer.component';
import { ClaimsSidebarComponent } from './claims-sidebar.component';
import { ClaimPickerPopoverComponent } from './claim-picker.popover.component';
import { NoteEditorComponent } from './note-editor.component';
import { OcrEditorComponent } from './ocr-editor.component';
import { TextInputOverlayComponent } from './text-input-overlay.component';
import { PaperPickerComponent } from './paper-picker.component';
import {
  AnnotationOverlayDirective,
  OverlayDrawCompleted,
  OverlayTextPointPicked,
} from './annotation-overlay.directive';

import { Project } from '../../../dtos/project';
import { Group } from '../../../dtos/group';
import { ProjectService } from '../../../services/project.service';
import { ProjectContextService } from '../../../services/project-context.service';
import { GroupService } from '../../../services/group.service';
import { ToastService } from '../../../services/toast.service';
import { ProjectStageNavComponent } from '../project-stage-nav/project-stage-nav.component';
import { ProjectState } from '../../../dtos/project-state';

import { PDF_SOURCE } from '../../../claim-annotator/ports/pdf-source.port';
import { CLAIMS_PROVIDER } from '../../../claim-annotator/ports/claims-provider.port';
import { ANNOTATION_REPOSITORY } from '../../../claim-annotator/ports/annotation-repository.port';
import { ANNOTATION_DRAFT_CACHE } from '../../../claim-annotator/ports/annotation-draft-cache.port';
import { USER_CONTEXT } from '../../../claim-annotator/ports/user-context.port';

import { InMemoryPdfSource } from '../../../claim-annotator/adapters/in-memory-pdf-source';
import { HttpAnnotationsAdapter } from '../../../claim-annotator/adapters/http-annotations';
import { LocalStorageDraftCache } from '../../../claim-annotator/adapters/local-storage-draft-cache';
import { AuthUserContext } from '../../../claim-annotator/adapters/auth-user-context';
import { QuestionnaireClaimsAdapter } from '../../../claim-annotator/adapters/questionnaire-claims.adapter';
import { QUESTIONNAIRE_PORT } from '../../../extraction-schema/ports/questionnaire.port';
import { HttpQuestionnaireAdapter } from '../../../extraction-schema/adapters/http-questionnaire.adapter';

import { AnnotationStore } from '../../../claim-annotator/domain/annotation-store';
import { AnnotationWorkflow } from '../../../claim-annotator/domain/annotation-workflow';
import { ClaimSetState } from '../../../claim-annotator/domain/claim-set-state';
import { Claim, PageGeometry } from '../../../claim-annotator/domain/types';

import { HttpPdfCorpus } from '../../../pdf-corpus/adapters/http-pdf-corpus';
import { PDF_CORPUS_PORT } from '../../../pdf-corpus/ports/pdf-corpus.port';
import { PdfCorpusService } from '../../../pdf-corpus/domain/pdf-corpus.service';

import { InMemoryProjectCorpus } from '../../../project-corpus/adapters/in-memory-project-corpus';
import { HybridProjectCorpus } from '../../../project-corpus/adapters/hybrid-project-corpus';
import { PROJECT_CORPUS_PORT } from '../../../project-corpus/ports/project-corpus.port';
import { ProjectPaper } from '../../../project-corpus/domain/types';

import { HasUnsavedChanges } from '../../../guards/unsaved-changes.guard';

@Component({
  selector: 'app-project-annotator-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    FormsModule,
    PdfViewerComponent,
    ClaimsSidebarComponent,
    ClaimPickerPopoverComponent,
    NoteEditorComponent,
    OcrEditorComponent,
    TextInputOverlayComponent,
    AnnotationOverlayDirective,
    PaperPickerComponent,
  ],
  providers: [
    InMemoryPdfSource,
    QuestionnaireClaimsAdapter,
    HttpAnnotationsAdapter,
    LocalStorageDraftCache,
    AuthUserContext,
    { provide: PDF_SOURCE, useExisting: InMemoryPdfSource },
    { provide: QUESTIONNAIRE_PORT, useClass: HttpQuestionnaireAdapter },
    { provide: CLAIMS_PROVIDER, useExisting: QuestionnaireClaimsAdapter },
    { provide: ANNOTATION_REPOSITORY, useClass: HttpAnnotationsAdapter },
    { provide: ANNOTATION_DRAFT_CACHE, useClass: LocalStorageDraftCache },
    { provide: USER_CONTEXT, useExisting: AuthUserContext },
    ClaimSetState,
    AnnotationStore,
    AnnotationWorkflow,
    HttpPdfCorpus,
    { provide: PDF_CORPUS_PORT, useExisting: HttpPdfCorpus },
    PdfCorpusService,
    InMemoryProjectCorpus,
    HybridProjectCorpus,
    { provide: PROJECT_CORPUS_PORT, useExisting: HybridProjectCorpus },
  ],
  templateUrl: './project-annotator-page.component.html',
})
export class ProjectAnnotatorPageComponent implements OnInit, HasUnsavedChanges {
  readonly store = inject(AnnotationStore);
  readonly ctx = inject(ProjectContextService);
  protected workflow = inject(AnnotationWorkflow);
  protected claims = inject(ClaimSetState);
  private pdfSource = inject(InMemoryPdfSource);
  private claimsProvider = inject(CLAIMS_PROVIDER);
  private pdfCorpus = inject(PdfCorpusService);
  private projectCorpus = inject(PROJECT_CORPUS_PORT);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private toast = inject(ToastService);

  private projectService = inject(ProjectService);
  private groupService = inject(GroupService);
  private router = inject(Router);

  readonly _project = signal<Project | null>(null);
  /** Public getter for backward-compat template access. */
  get project(): Project | null {
    return this._project();
  }
  set project(value: Project | null) {
    this._project.set(value);
  }
  group: Group | null = null;

  readonly pageGeometries = signal<Map<number, PageGeometry>>(new Map());
  readonly pageElements = signal<Map<number, HTMLElement>>(new Map());
  readonly drawMode = signal<'box' | 'text' | null>(null);
  readonly submitting = signal<boolean>(false);
  readonly lastError = signal<string | null>(null);
  readonly questionsLoading = signal<boolean>(false);
  readonly questionsError = signal<string | null>(null);
  readonly hasQuestions = computed(() => {
    const id = this.store.activeDocumentId();
    return id ? this.claims.getForDocument(id).length > 0 : false;
  });

  /** True when the project is complete: annotations are read-only. */
  readonly isReadOnly = computed(() => this._project()?.state === ProjectState.COMPLETED);

  readonly projectId = signal<string>('');
  readonly groupId = signal<string | null>(null);
  readonly papers = signal<ProjectPaper[]>([]);
  readonly selectedPaperId = signal<string | null>(null);
  readonly materializing = signal(false);
  private loadToken = 0;
  private readonly resolvedPdfIds = new Map<string, number>();

  /** CanDeactivate guard interface: returns true when it is safe to leave. */
  hasUnsavedChanges(): boolean {
    // Read-only projects cannot be saved, so a dirty draft is not a blocker.
    // The localStorage cache still preserves the work in case the project
    // transitions back to an editable state.
    return !this.isReadOnly() && this.store.hasAnyUnsavedChanges();
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.isReadOnly() && this.store.hasAnyUnsavedChanges()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const isCtrlOrCmd = event.ctrlKey || event.metaKey;
    if (!isCtrlOrCmd || event.key !== 's') return;
    // Always prevent the browser's Save-page dialog (R9).
    event.preventDefault();
    // Skip submit while any editor overlay is open: the half-typed edit is not
    // yet committed, so persisting now would silently drop it.
    const editorOpen =
      this.workflow.pendingTextPoint() !== null ||
      this.workflow.pendingDraft() !== null ||
      this.workflow.editingNoteForId() !== null ||
      this.workflow.editingOcrForId() !== null;
    if (editorOpen) return;
    // Only actually save if dirty, not already submitting, and not read-only.
    if (this.store.hasUnsubmittedChanges() && !this.submitting() && !this.isReadOnly()) {
      void this.onSubmit();
    }
  }

  ngOnInit(): void {
    const id = this.route.parent?.snapshot.paramMap.get('projectId') ?? '';
    this.projectId.set(id);
    if (!id) return;

    this.projectService
      .getProjectById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (proj) => {
          this.project = proj;
          if (proj) {
            this.groupService
              .getGroupById(proj.groupId)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: (grp) => (this.group = grp),
                error: (err) => console.error('Failed to load group details', err),
              });
          }

          this.projectCorpus
            .getProjectSummary(id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (s) => {
                this.groupId.set(s.groupId);
                this.loadExtractionPapers();
              },
              error: () => this.lastError.set('Failed to load the project summary.'),
            });
        },
        error: () => this.lastError.set('Failed to load the project.'),
      });
  }

  loadExtractionPapers(): void {
    const id = this.projectId();
    this.projectCorpus
      .listPapers(id, {}, { page: 0, size: 200 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        // Only papers that are not explicitly excluded during the second-stage screening are annotatable.
        // Papers with a stored pdfId open directly; url-only papers trigger lazy materialization.
        next: (page) => this.papers.set(page.items.filter((p) => p.secondStageIncluded !== false)),
        error: () => this.lastError.set('Failed to load the project papers.'),
      });
  }

  ngOnDestroy(): void {}

  goBack(): void {
    this.router.navigate(['/projects', this.projectId()]);
  }

  async onPaperSelected(paper: ProjectPaper): Promise<void> {
    const token = ++this.loadToken;
    this.lastError.set(null);
    this.selectedPaperId.set(paper.id);
    const docKey = `paper:${paper.id}`;
    const gid = this.groupId();
    try {
      let pdfId: number;
      if (paper.pdfId != null || this.resolvedPdfIds.has(paper.id)) {
        if (gid == null) {
          this.lastError.set('Project group is still loading; try again in a moment.');
          return;
        }
        pdfId = paper.pdfId != null ? Number(paper.pdfId) : this.resolvedPdfIds.get(paper.id)!;
      } else if (paper.pdfUrl != null) {
        if (gid == null) {
          this.lastError.set('Project group is still loading; try again in a moment.');
          return;
        }
        this.materializing.set(true);
        const resolvedId = await firstValueFrom(
          this.pdfCorpus.materializePdf(Number(gid), Number(paper.id)),
        );
        if (token !== this.loadToken) return;
        this.resolvedPdfIds.set(paper.id, resolvedId);
        pdfId = resolvedId;
      } else {
        this.pdfSource.clear();
        this.lastError.set('This paper has no PDF to annotate.');
        return;
      }
      const blob = await firstValueFrom(this.pdfCorpus.fetchBytes(Number(gid), pdfId));
      if (token !== this.loadToken) return;
      this.openPaperDocument(docKey, 'file', paper.title || paper.id);
      this.pdfSource.setBlob(blob, paper.title || `${paper.id}.pdf`);
    } catch (e) {
      if (token !== this.loadToken) return;
      this.pdfSource.clear();
      this.lastError.set((e as Error).message || 'Failed to load the PDF for this paper.');
      return;
    } finally {
      if (token === this.loadToken) this.materializing.set(false);
    }
    if (token !== this.loadToken) return;
    await this.loadClaimsForDocument(docKey);
    await this.store.hydrateFromRepository(docKey);
    // R5: re-check token after awaits before applying state or toasts.
    if (token !== this.loadToken) return;
    // C8: show recovery toast if the store restored a draft for this document.
    if (this.store.recoveredDocuments().has(docKey)) {
      this.toast.info(
        'Recovered unsaved annotations',
        'Your previous unsaved work has been restored. Save to keep it.',
      );
    }
    // R11: always acknowledge, whether or not toast was shown.
    this.store.acknowledgeRecovery(docKey);
  }

  private async loadClaimsForDocument(docKey: string): Promise<void> {
    this.questionsError.set(null);
    this.questionsLoading.set(true);
    try {
      const claims = await this.claimsProvider.loadFor(docKey);
      this.claims.setForDocument(docKey, claims);
    } catch (e) {
      this.claims.setForDocument(docKey, []);
      this.questionsError.set(questionLoadErrorMessage(e));
    } finally {
      this.questionsLoading.set(false);
    }
  }

  /** PDF viewer load failure (e.g. corrupted blob). */
  onViewerLoadFailed(): void {
    this.lastError.set('The PDF failed to load in the viewer.');
  }

  private openPaperDocument(docKey: string, kind: 'file' | 'url', ref: string): void {
    this.store.openDocument({
      source_kind: kind,
      source_ref: ref,
      content_hash: docKey,
      page_count: 0,
    });
  }

  toggleBoxMode(): void {
    if (!this.hasQuestions() || this.isReadOnly()) return;
    this.drawMode.update((m) => (m === 'box' ? null : 'box'));
  }
  toggleTextMode(): void {
    if (!this.hasQuestions() || this.isReadOnly()) return;
    this.drawMode.update((m) => (m === 'text' ? null : 'text'));
  }

  onPageRendered(ev: PageRenderedEvent): void {
    const vp = ev.viewport as {
      convertToPdfPoint(x: number, y: number): [number, number];
      convertToViewportPoint(x: number, y: number): [number, number];
      viewBox: [number, number, number, number];
    };
    const geom: PageGeometry = {
      pageWidthPdf: vp.viewBox[2] - vp.viewBox[0],
      pageHeightPdf: vp.viewBox[3] - vp.viewBox[1],
      toPdfPoint: (s) => {
        const [x, y] = vp.convertToPdfPoint(s.x, s.y);
        return { x, y };
      },
      toScreenPoint: (p) => {
        const [x, y] = vp.convertToViewportPoint(p.x, p.y);
        return { x, y };
      },
    };
    this.pageGeometries.update((m) => {
      const next = new Map(m);
      next.set(ev.pageNumber, geom);
      return next;
    });
    this.pageElements.update((m) => {
      const next = new Map(m);
      next.set(ev.pageNumber, ev.pageElement);
      return next;
    });
  }

  geometryFor(page: number): PageGeometry | null {
    return this.pageGeometries().get(page) ?? null;
  }
  pageElementFor(page: number): HTMLElement | null {
    return this.pageElements().get(page) ?? null;
  }
  pages(): number[] {
    return Array.from(this.pageGeometries().keys()).sort((a, b) => a - b);
  }
  activeDocumentId(): string {
    return this.store.activeDocumentId() ?? '';
  }
  currentClaims(): Claim[] {
    const id = this.store.activeDocumentId();
    return id ? this.claims.getForDocument(id) : [];
  }

  onOverlayDraw(ev: OverlayDrawCompleted): void {
    this.workflow.proposeBox(ev.pageNumber, ev.rect);
  }
  onOverlayTextPoint(ev: OverlayTextPointPicked): void {
    this.workflow.proposeTextPoint({
      pageNumber: ev.pageNumber,
      pdfX: ev.pdfX,
      pdfY: ev.pdfY,
      clientX: ev.clientX,
      clientY: ev.clientY,
    });
  }
  onTextInputConfirmed(payload: { text: string; width: number; height: number }): void {
    const point = this.workflow.pendingTextPoint();
    if (!point) return;
    const geom = this.geometryFor(point.pageNumber);
    if (!geom) {
      this.workflow.cancelTextInput();
      return;
    }
    const p1 = geom.toPdfPoint({ x: 0, y: 0 });
    const p2 = geom.toPdfPoint({ x: payload.width, y: payload.height });
    const pdfWidth = Math.abs(p2.x - p1.x);
    const pdfHeight = Math.abs(p2.y - p1.y);
    this.workflow.provideTextContent({ text: payload.text, pdfWidth, pdfHeight });
  }
  onTextInputCancelled(): void {
    this.workflow.cancelTextInput();
  }
  onClaimPicked(claimId: string): void {
    this.workflow.commitWithClaim(claimId);
  }
  onClaimPickerCancelled(): void {
    this.workflow.cancelDraft();
  }
  onEditNoteRequested(event: { id: string; clientX: number; clientY: number }): void {
    this.workflow.editNote(event.id);
  }
  onDeleteAnnotation(annotationId: string): void {
    this.store.removeAnnotation(annotationId);
  }
  onNoteSaved(value: string): void {
    this.workflow.saveNote(value);
  }
  onNoteEditorCancelled(): void {
    this.workflow.cancelNoteEdit();
  }
  onEditOcrRequested(event: { id: string; clientX: number; clientY: number }): void {
    this.workflow.editOcr(event.id);
  }
  onOcrSaved(value: string): void {
    this.workflow.saveOcr(value);
  }
  onOcrEditorCancelled(): void {
    this.workflow.cancelOcrEdit();
  }

  async onSubmit(): Promise<void> {
    const docId = this.store.activeDocumentId();
    if (!docId) return;
    this.lastError.set(null);
    this.submitting.set(true);
    try {
      await this.store.submitChanges(docId);
      this.toast.success('Annotations saved', 'Your annotations have been saved successfully.');
    } catch (e) {
      this.lastError.set(this.saveErrorMessage(e));
    } finally {
      this.submitting.set(false);
    }
  }

  /** Map a save error to a human-readable message. Handles 401 specially. */
  saveErrorMessage(e: unknown): string {
    if (e instanceof HttpErrorResponse) {
      if (e.status === 401) {
        // Do NOT clear the draft: store keeps it so the user can re-login and retry.
        this.router.navigate(['/login']);
        return 'Your session has expired. Please log in again.';
      }
      if (e.status === 0) {
        return 'Could not reach the server. Check your connection and try again.';
      }
      if (e.status >= 500) {
        return 'The server ran into a problem. Please try again in a moment.';
      }
      return 'Saving failed. Please try again.';
    }
    return 'Saving failed: ' + ((e as Error).message || 'unknown error');
  }

  onDiscard(): void {
    const docId = this.store.activeDocumentId();
    if (!docId) return;
    this.lastError.set(null);
    this.store.discardChanges(docId);
  }
}

function questionLoadErrorMessage(e: unknown): string {
  if (e instanceof HttpErrorResponse && e.status === 0) {
    return 'Could not reach the server to load the extraction questions.';
  }
  return 'Could not load the extraction questions.';
}
